export function normalizeDisplayDate(value: string): string {
  const source = value.trim();

  if (!source) {
    return '';
  }

  const compact = /^(\d{2})(\d{2})(\d{4})$/.exec(source);
  if (compact) {
    return `${compact[1]}.${compact[2]}.${compact[3]}`;
  }

  const dotted = /^(\d{1,2})[.](\d{1,2})[.](\d{4})$/.exec(source);
  if (dotted) {
    return `${dotted[1].padStart(2, '0')}.${dotted[2].padStart(2, '0')}.${dotted[3]}`;
  }

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(source);
  if (iso) {
    return `${iso[3].padStart(2, '0')}.${iso[2].padStart(2, '0')}.${iso[1]}`;
  }

  return source;
}

export function toIsoDate(value: string): string | null {
  const normalized = normalizeDisplayDate(value);
  const match = /^(\d{2})[.](\d{2})[.](\d{4})$/.exec(normalized);

  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return `${year.toString().padStart(4, '0')}-${match[2]}-${match[1]}`;
}

export function isoDateToDisplay(value: string | null | undefined): string {
  const source = value?.trim() ?? '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(source);

  if (!match) {
    return '';
  }

  return `${match[3]}.${match[2]}.${match[1]}`;
}

export function isValidTime(value: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(value);

  if (!match) {
    return false;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

export function timeToMinutes(value: string): number | null {
  if (!isValidTime(value)) {
    return null;
  }

  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesToTime(value: number): string {
  const normalized = Math.max(0, Math.min(value, 24 * 60 - 1));
  const hoursText = Math.floor(normalized / 60).toString().padStart(2, '0');
  const minutesText = (normalized % 60).toString().padStart(2, '0');
  return `${hoursText}:${minutesText}`;
}

export function buildTimeOptions(stepMinutes: number): string[] {
  const options: string[] = [];
  const step = Math.trunc(stepMinutes) > 0 && Math.trunc(stepMinutes) <= 60
    ? Math.trunc(stepMinutes)
    : 15;

  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const hoursText = Math.floor(minutes / 60).toString().padStart(2, '0');
    const minutesText = (minutes % 60).toString().padStart(2, '0');
    options.push(`${hoursText}:${minutesText}`);
  }

  return options;
}

export function buildRangedTimeOptions(
  stepMinutes: number,
  minTime: string | null | undefined,
  maxTime: string | null | undefined,
): string[] {
  const step = Math.trunc(stepMinutes) > 0 && Math.trunc(stepMinutes) <= 60
    ? Math.trunc(stepMinutes)
    : 15;
  const startMinutes = timeToMinutes(minTime ?? '') ?? 0;
  const endMinutes = timeToMinutes(maxTime ?? '') ?? (24 * 60 - step);
  const options: string[] = [];

  if (endMinutes < startMinutes) {
    return buildTimeOptions(step);
  }

  for (let minutes = startMinutes; minutes <= endMinutes; minutes += step) {
    options.push(minutesToTime(minutes));
  }

  if (maxTime && isValidTime(maxTime) && !options.includes(maxTime)) {
    options.push(maxTime);
  }

  return options;
}

export function currentIsoDate(): string {
  const date = new Date();
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function currentDisplayDate(): string {
  return isoDateToDisplay(currentIsoDate());
}

export function parseDateTimeValue(value: string | null | undefined): { dateText: string; timeText: string } {
  const source = value?.trim() ?? '';
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(source);

  if (!match) {
    return { dateText: '', timeText: '' };
  }

  return {
    dateText: `${match[3]}.${match[2]}.${match[1]}`,
    timeText: `${match[4]}:${match[5]}`,
  };
}
